import { Location } from '@angular/common';
import { Inject, Injectable, isDevMode } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, NavigationStart, Router, RouterStateSnapshot } from '@angular/router';
import { debounceTime, filter, lastValueFrom, map, merge, Observable, shareReplay, skip, Subject, switchMap, take, tap } from 'rxjs';
import { gridElements } from 'src/app/grid.elements';
import { RouteConfig } from 'src/app/routes';
import { SearchClient, SearchProductResponseOfProductCardModel, SearchRequestModel } from 'src/business/services/client.generated';
import { BASE_URL, LANGUAGE } from 'src/core-lib/misselaineous/boot.tokens';
import { GA4 } from 'src/externals/tag-manager/models/ga4.model';
import { OverlayEvent } from 'src/layout/overlay/models/overlay.interface';
import { MarketSegment } from 'src/utils/market-segment.util';
import { SerializerUtil } from 'src/utils/serializer.util';
import { ContentCacheUtils, StorageUtil } from 'src/utils/storage.util';
import { UrlUtil } from 'src/utils/url.util';
import { ContentMetadata } from '../models/content-metadata.model';




function mapPageTitle(page: any): string {
    const metaName = page?._meta?.name;
    const pageTitle = page?.pageTitle;
    return pageTitle || metaName || '';
}

function mapPageMeta(data: ContentMetadata) {
    document.head.querySelectorAll('meta[dynamictag]')?.forEach(e => e.remove());
    if (data == null)
        return [];
    return Object.keys(data)
        .map(tag => ({
            name: tag,
            property: tag,
            content: data[tag]
        }))
}

function setCanonical(canonical: string) {
    document.head.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical)
}

function prerender() {
    if (window != null)
        if (!window['prerenderReady']) {
            // give the page a little extra time to draw if external requests are slow
            setTimeout(() => {
                try {
                    var _s = document.createElement('script');
                    _s.innerHTML = "window['prerenderReady'] = true;";
                    _s.type = "text/javascript";
                    document.head.append(_s);

                } catch (error) {
                    console.error('prerenderReady Falied');
                }
            }, 5000);
        }
}

type GridLayout = { layout?: GridLayout[], alias: string }
@Injectable({
    providedIn: 'root'
})
export class ContentResolver {

    protected _updaters = [
        page => StorageUtil.set('page', page),
        page => setCanonical(page?._meta?.canonical),
        page => this.title.setTitle(mapPageTitle(page)),
        page => mapPageMeta(page?.htmlSeoMetaTags).forEach(tag => this.meta.addTag(tag)),
        page => document.querySelectorAll('dialog').forEach(_d => _d.close()),
        page => prerender(),
        page => GA4.track({ __event_name: 'virtual_pageview', url: page._meta.url } as GA4.VirtualPageview),
        page => OverlayEvent(null),
    ];

    constructor(
        protected title: Title,
        protected meta: Meta,
    ) {}

    async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<any> {
        let page;
        if (RouteConfig.isFirst) {
            RouteConfig.isFirst = false;
            page = await ContentCacheUtils.page<unknown>();
        } else {
            var _url = new URL(state.url, window.location.origin);
            _url.searchParams.set('type', 'json');
            var response = await fetch(_url.href);
            if (!response.ok)
                return { page: { _meta: { alias: 'notFoundPage' } } }
            var _json = await (response).json();

            page = _json;
        }
        if ('layout' in page) {
            this.preloadGrid(page)
        }
        this._updaters.forEach(updater => updater(page));
        return { page };
    }

    private async preloadGrid(page: GridLayout, loaded = {}) {
        try {
            if (page.alias in loaded) {} else {
                await gridElements[page.alias]();
                loaded[page.alias] = true;
            }
        } catch {}
        if ('layout' in page && Array.isArray(page.layout)) {
            await page.layout.map(async layout => await this.preloadGrid(layout))
        }
    }
}

export type SearchResult = null | Partial<SearchProductResponseOfProductCardModel> & { state?: string };

@Injectable({
    providedIn: 'root'
})
export class CatalogContentAndSearchResolver extends ContentResolver {
    private static cache: { [key: string]: Promise<SearchResult> } = {};
    private _searchClient: SearchClient;
    private _request: Subject<SearchRequestModel>;
    private _result$: Observable<SearchResult>;
    public result$: Observable<SearchResult>;
    public size: number = 24;
    _tags: string[][];
    _interval: number;
    async searchFn(model: SearchRequestModel) {
        let str = JSON.stringify(model) + this._catalog + MarketSegment.getSelected();
        if (CatalogContentAndSearchResolver.cache[str])
            return CatalogContentAndSearchResolver.cache[str];
        CatalogContentAndSearchResolver.cache[str] = new Promise(async (resolve) => {
            var resp: SearchResult = await this._searchClient.search(this._catalog, model).catch(() => ({
                facets: [],
                state: 'error',
                results: [],
                sortings: [],
                totalResults: 0,
            }));
            if (resp.state == null)
                resp.state = 'success';

            setTimeout(() => {
                delete CatalogContentAndSearchResolver.cache[str];
            }, 2000);
            resolve(resp)
        })
        return CatalogContentAndSearchResolver.cache[str]
    }

    constructor(
        title: Title,
        meta: Meta,
        @Inject(BASE_URL)
        baseUrl: string,
        @Inject(LANGUAGE)
        private _catalog: string,
        private _location: Location,
        private _router: Router,
    ) {
        super(title, meta);
        this._searchClient = new SearchClient(baseUrl);

        this._request = new Subject<SearchRequestModel>();

        let requestSearchResponse = this._request.pipe(
            debounceTime(600),
            filter(_ => _ != null),
            map(model => this.searchFn(model)),
            switchMap(_ => _),
            shareReplay(1)
        );
        let cleanResponseOnPageChange = this._router.events.pipe(
            filter(e => e instanceof NavigationStart),
            map(() => null as SearchResult),
        );
        this._result$ = merge(
            requestSearchResponse,
            cleanResponseOnPageChange
        );
        this.result$ = this._result$.pipe(shareReplay(1));
    }

    async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<any> {
        var page = (await super.resolve(route, state))?.page;
        this._tags = page?._meta?.alias === "catalogPage" ? page.filters.map(filter => filter.productTags) : [];
        this._interval = page?.interval;
        var result = await this.search(this.urlModel(state.url));
        return { page, result, };
    }

    async search(model: SearchRequestModel, skipN: number = 0) {
        var resp = lastValueFrom(this._result$.pipe(skip(skipN), take(1)));
        this._request.next(model);
        return resp;
    }

    updateUrlByForm(formData: FormData): string {
        var searchParams = new URLSearchParams(formData as any);
        var _url = UrlUtil.safeUrl(searchParams);
        var url = SerializerUtil.serialize(_url);
        this._location.go(url);
        this._router.getCurrentNavigation();
        this.search(this.urlModel(_url.href));
        return url;
    }

    tagsModel(tags: string[][], size: number): SearchRequestModel {
        var searchTerms = '';
        var from = 0;
        return { from, size, tags, searchTerms };
    }

    formModel(formData: FormData): SearchRequestModel {
        var searchParams = new URLSearchParams(formData as any);
        var size = +searchParams.get('size');
        size = size > 0 ? size : this.size;
        var tags = [];
        var from = +(searchParams.get(UrlUtil.PAGE_PREFIX) || 0) * size;
        var sorting = searchParams.get(UrlUtil.SORT_PREFIX) || '';
        var searchTerms = searchParams.get(UrlUtil.SEARCH_PREFIX) || '';
        var facets = this.facets(Array.from(searchParams.entries()));
        var priceRangeInterval = this._interval;
        return { from, size, sorting, tags, searchTerms, facets, priceRangeInterval };
    }

    urlModel(url: string): SearchRequestModel {
        var _url = new URL(url, window.origin);
        var size = this.size;
        var tags = this._tags;
        var from = +(_url.searchParams.get(UrlUtil.PAGE_PREFIX) || 0) * size;
        var sorting = _url.searchParams.get(UrlUtil.SORT_PREFIX) || '';
        var searchTerms = _url.searchParams.get(UrlUtil.SEARCH_PREFIX) || '';
        var facets = this.facets(Array.from(_url.searchParams.entries()));
        var priceRangeInterval = this._interval;
        return { from, size, sorting, tags, searchTerms, facets, priceRangeInterval };
    }

    facets(entries: [key: string, value: string][]): { [key: string]: string[] } {
        let facets: any = {};
        entries.forEach(([key, value]) => {
            if (key.startsWith(UrlUtil.FACET_PREFIX)) {
                const cleanKey = key.replace(UrlUtil.FACET_PREFIX, '');
                if (facets[cleanKey] == null)
                    facets[cleanKey] = [];
                facets[cleanKey].push(value)
            }
        });
        return facets;
    }
}

@Injectable({
    providedIn: 'root'
})
export class UnknownContentResolver extends ContentResolver {
    constructor(
        private router: Router,
        title: Title,
        meta: Meta,
    ) {
        super(title, meta);
    }
    async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<any> {
        var content = await super.resolve(route, state);
        if (content?.page?._meta?.alias != 'notFoundPage') {
            var config = RouteConfig.pages[content?.page?._meta?.alias];
            if (config != null) {
                var _StateUrl = new URL(state?.url, window.location.origin);
                var _url = new URL(content?.page?._meta?.url, window.location.origin);
                var _link = _url.href?.replace(window.location.origin, '').replace('/', '');
                this.router.config = [...this.router.config.slice(0, this.router.config.length - 1), config(_link), ...this.router.config.slice(this.router.config.length - 1)];
                _url.search = _StateUrl.search;
                _url.hash = _StateUrl.hash;
                var __link = _url.href?.replace(window.location.origin, '');
                setTimeout(() => this.router.navigateByUrl(__link), 200);
            } else {
                this.router.navigateByUrl('404', { browserUrl: globalThis.location.href })
            }
        }
        return { page: content?.page, }
    }

}

